import React from 'react';
import { useReferral } from '../contexts/ReferralContext';
import { Gift, X } from 'react-feather';

interface ReferralBannerProps {
  variant?: 'default' | 'compact';
  onApply?: () => void;
  onDismiss?: () => void;
  className?: string;
}

/**
 * Banner shown to users who have a pending referral code
 * Displays on login/registration pages
 */
export const ReferralBanner: React.FC<ReferralBannerProps> = ({
  variant = 'default',
  onApply,
  onDismiss,
  className = '',
}) => {
  const { pendingCode, isLoading, error, applyCode, clearPendingCode, referralInfo } = useReferral();

  // Don't show if no pending code, already has code, or code is locked
  if (!pendingCode || referralInfo?.hasReferralCode || referralInfo?.isLocked) {
    return null;
  }

  const handleApply = async () => {
    const result = await applyCode(pendingCode, 'web');
    if (result.success && onApply) {
      onApply();
    }
  };

  const handleDismiss = () => {
    clearPendingCode();
    if (onDismiss) {
      onDismiss();
    }
  };

  // Compact variant for login/signup pages
  if (variant === 'compact') {
    return (
      <div 
        style={{
          background: 'linear-gradient(135deg, #fff7ed 0%, #ffedd5 100%)',
          border: '1px solid #fed7aa',
          borderRadius: '12px',
          padding: '12px 16px',
          marginBottom: '16px',
          fontFamily: 'Manrope, sans-serif'
        }}
      >
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px'
          }}>
            <div style={{
              width: '32px',
              height: '32px',
              borderRadius: '8px',
              background: 'linear-gradient(135deg, #f9550d 0%, #e64a0c 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0
            }}>
              <Gift size={16} color="#fff" />
            </div>
            <div>
              <span style={{
                fontSize: '14px',
                color: '#9a3412',
                fontWeight: '500'
              }}>
                Referral code{' '}
                <span style={{
                  fontFamily: 'monospace',
                  fontWeight: '700',
                  color: '#c2410c',
                  background: '#fff',
                  padding: '2px 6px',
                  borderRadius: '4px',
                  fontSize: '13px'
                }}>
                  {pendingCode}
                </span>
                {' '}will be applied
              </span>
            </div>
          </div>
          <button
            onClick={handleDismiss}
            style={{
              background: 'none',
              border: 'none',
              color: '#c2410c',
              cursor: 'pointer',
              padding: '4px',
              borderRadius: '4px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              opacity: 0.7,
              transition: 'opacity 0.2s'
            }}
            onMouseEnter={(e) => e.currentTarget.style.opacity = '1'}
            onMouseLeave={(e) => e.currentTarget.style.opacity = '0.7'}
          >
            <X size={16} />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div 
      style={{
        background: 'linear-gradient(135deg, #f9550d 0%, #e64a0c 100%)',
        borderRadius: '12px',
        padding: '16px 20px',
        boxShadow: '0 4px 12px rgba(249, 85, 13, 0.25)',
        fontFamily: 'Manrope, sans-serif'
      }}
    >
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: '12px'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px'
        }}>
          <div style={{
            width: '40px',
            height: '40px',
            borderRadius: '10px',
            background: 'rgba(255,255,255,0.2)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0
          }}>
            <Gift size={20} color="#fff" />
          </div>
          <div>
            <p style={{
              margin: 0,
              fontSize: '15px',
              fontWeight: '600',
              color: '#fff'
            }}>
              Referral Code Applied!
            </p>
            <p style={{
              margin: '2px 0 0 0',
              fontSize: '13px',
              color: 'rgba(255,255,255,0.85)'
            }}>
              Code{' '}
              <span style={{
                fontFamily: 'monospace',
                fontWeight: '700',
                background: 'rgba(255,255,255,0.2)',
                padding: '2px 6px',
                borderRadius: '4px'
              }}>
                {pendingCode}
              </span>
              {' '}is ready to be linked
            </p>
            {error && (
              <p style={{
                margin: '4px 0 0 0',
                fontSize: '12px',
                color: '#fecaca'
              }}>
                {error}
              </p>
            )}
          </div>
        </div>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          <button
            onClick={handleDismiss}
            disabled={isLoading}
            style={{
              background: 'transparent',
              border: 'none',
              color: 'rgba(255,255,255,0.7)',
              fontSize: '13px',
              fontWeight: '500',
              cursor: 'pointer',
              padding: '8px 12px',
              fontFamily: 'Manrope, sans-serif'
            }}
          >
            Dismiss
          </button>
          <button
            onClick={handleApply}
            disabled={isLoading}
            style={{
              background: '#fff',
              color: '#f9550d',
              border: 'none',
              borderRadius: '8px',
              padding: '10px 16px',
              fontSize: '13px',
              fontWeight: '600',
              cursor: isLoading ? 'not-allowed' : 'pointer',
              opacity: isLoading ? 0.7 : 1,
              fontFamily: 'Manrope, sans-serif',
              transition: 'transform 0.2s'
            }}
          >
            {isLoading ? 'Applying...' : 'Apply Now'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ReferralBanner;
